import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import Hero from "../components/Hero";
import ProductCard from "../components/ProductCard";
import { getFeaturedProducts } from "../lib/firestoreProducts";
import { ArrowUpRight } from "lucide-react";

const EMBROIDERY_MACRO = '/assets/images/embroidery-macro.jpeg';
const COLLAGE_IMAGES = [
  '/assets/images/collage_img_1.jpg',
  '/assets/images/collage_img_4.jpg',
  '/assets/images/collage_img_3.jpg',
  '/assets/images/collage_img_41.png',
];

export default function HomePage() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    getFeaturedProducts(4)
      .then((items) => setFeatured(items))
      .catch(() => {});
  }, []);

  return (
    <>
      <Hero />

      {/* Manifesto / Philosophy */}
      <section className="px-6 md:px-12 py-24 md:py-40 bg-white" data-testid="philosophy-section">
        <div className="max-w-6xl">
          <div className="label-eyebrow mb-8 flex items-center gap-3">
            <span className="w-10 h-px bg-black" /> 01 — Philosophy
          </div>
          <h2 className="font-display text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.95] max-w-5xl">
            A cap should be
            <span className="text-[#C9A961] italic font-light"> patient enough </span>
            to be worn forever, and
            <span className="italic font-light"> precise enough </span>
            to be remembered.
          </h2>
          <div className="mt-16 grid md:grid-cols-3 gap-12 md:gap-8 max-w-5xl">
            {[
              { n: "24", l: "stitches per crown emblem" },
              { n: "11", l: "hand-finishing steps" },
              { n: "200", l: "max units per edition" },
            ].map((s) => (
              <div key={s.n} className="border-t border-black pt-6">
                <div className="font-display text-6xl tracking-tighter">{s.n}</div>
                <div className="label-eyebrow text-black/50 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Embroidery macro split */}
      <section className="grid md:grid-cols-2 border-y border-[#E5E3DE]" data-testid="macro-section">
        <div className="relative aspect-[4/5] md:aspect-auto md:min-h-[80vh] overflow-hidden">
          <motion.div
            className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-1"
            initial={{ scale: 1.06 }}
            whileInView={{ scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1] }}
          >
            {COLLAGE_IMAGES.map((src, i) => (
              <motion.img
                key={src}
                src={src}
                alt={`Collage image ${i + 1}`}
                className="w-full h-full object-cover"
                style={{ width: '100%', height: '100%' }}
                loading="lazy"
              />
            ))}
          </motion.div>
        </div>
        <div className="bg-[#0A0A0A] text-white p-8 md:p-16 lg:p-24 flex flex-col justify-center">
          <div className="label-eyebrow text-white/50 mb-6">02 — The detail</div>
          <h3 className="font-display text-4xl md:text-6xl tracking-tight leading-[0.95]">
            Look closer.<br />
            That's the whole point.
          </h3>
          <p className="mt-8 text-white/70 max-w-md leading-relaxed">
            Every cap is rendered in individual stitches, programmed in-house,
            then re-walked by hand. The thread is mercerised cotton — not polyester —
            so the sheen ages instead of plasticising.
          </p>
          <Link to="/shop" data-testid="cta-shop-link" className="mt-10 inline-flex items-center gap-2 text-sm border-b border-white/40 pb-1 w-fit hover:border-[#C9A961] hover:text-[#C9A961] transition-colors">
            See the collection <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>

      {/* Featured products */}
      <section className="px-6 md:px-12 py-24 md:py-32" data-testid="featured-section">
        <div className="flex items-end justify-between mb-12 md:mb-16 flex-wrap gap-6">
          <div>
            <div className="label-eyebrow mb-4">03 — Current edition</div>
            <h2 className="font-display text-4xl md:text-6xl tracking-tight">Selected pieces.</h2>
          </div>
          <Link to="/shop" className="label-eyebrow link-underline">View entire shop →</Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 md:gap-10">
          {featured.map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* B2B portal — Royal House */}
      <section
        data-testid="b2b-teaser"
        className="relative bg-[#0A0A0A] text-white overflow-hidden border-y border-[#C9A961]/20"
      >
        {/* faint heraldic grid */}
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.04] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#C9A961 1px, transparent 1px), linear-gradient(90deg, #C9A961 1px, transparent 1px)",
            backgroundSize: "120px 120px",
          }}
        />

        {/* Top crest bar */}
        <div className="relative z-10 flex items-center justify-between px-6 md:px-12 pt-10 md:pt-14 pb-4">
          <div className="flex items-center gap-3">
            <span className="w-10 h-px bg-[#C9A961]" />
            <span className="label-eyebrow text-[#C9A961]">House of Crown · Atelier B2B</span>
          </div>
          <span className="label-eyebrow text-white/40 hidden md:inline">— Established MMXXVI —</span>
        </div>

        <div className="relative z-10 grid md:grid-cols-12 gap-0 px-6 md:px-12 pb-20 md:pb-28">
          {/* Left: editorial copy */}
          <div className="md:col-span-7 lg:col-span-7 pt-10 md:pt-16 md:pr-12 flex flex-col">
            <div className="flex items-center gap-4 mb-8">
              <svg viewBox="0 0 60 60" className="w-12 h-12 text-[#C9A961]" fill="currentColor">
                {/* simple crown crest */}
                <path d="M30 6 L36 22 L48 14 L46 34 L14 34 L12 14 L24 22 Z" />
                <rect x="14" y="38" width="32" height="3" />
                <rect x="14" y="44" width="32" height="2" />
              </svg>
              <div>
                <div className="label-eyebrow text-[#C9A961]">No. 01 / Ledger</div>
                <div className="text-white/60 text-sm">For founders, marketing leaders &amp; private clients</div>
              </div>
            </div>

            <h2 className="font-display text-5xl md:text-7xl lg:text-[6.5rem] tracking-tighter leading-[0.88]">
              Onboarding Caps.<br />
              <span className="italic font-light text-[#C9A961]">Brand drops.</span><br />
              Founder gifts.<br />
              <span className="text-white/50 italic font-light">Embroidered,</span> not printed.
            </h2>

            <div className="mt-10 max-w-lg text-white/70 leading-relaxed">
              A private commission service for teams who refuse the generic. Numbered runs
              from 50 units, hand-finished and presented in a custom black gift box —
              ready for your first day, your conference floor, or the founder's desk.
            </div>

            {/* Ledger of services — royal list */}
            <div className="mt-12 grid grid-cols-2 gap-x-8 gap-y-4 max-w-lg border-t border-[#C9A961]/30 pt-8">
              {[
                ["I.", "The Onboarding Cap"],
                ["II.", "Conference drops"],
                ["III.", "Founder gifts"],
                ["IV.", "Numbered editions"],
                ["V.", "Brand collaborations"],
                ["VI.", "Private commissions"],
              ].map(([n, l]) => (
                <div key={n} className="flex items-baseline gap-3 text-sm">
                  <span className="text-[#C9A961] font-display text-base w-6">{n}</span>
                  <span className="text-white/80">{l}</span>
                </div>
              ))}
            </div>

            <div className="mt-14 flex flex-col sm:flex-row sm:items-center gap-6">
              <Link
                to="/custom"
                data-testid="cta-b2b-link"
                className="inline-flex items-center gap-3 bg-[#C9A961] text-black px-8 py-5 text-sm tracking-[0.18em] uppercase font-bold hover:bg-white transition-colors w-fit"
              >
                Commission a piece <ArrowUpRight size={16} />
              </Link>
              <div className="flex items-center gap-3 text-xs text-white/50">
                <span className="w-8 h-px bg-white/30" />
                <span>From 50 units · Lead time 14 days</span>
              </div>
            </div>
          </div>

          {/* Right: ceremonial image — the gift box */}
          <div className="md:col-span-5 lg:col-span-5 relative mt-12 md:mt-0">
            <div className="relative">
              {/* Wax seal style stamp */}
              <div className="absolute -top-6 -left-6 z-20 w-24 h-24 rounded-full bg-[#C9A961] text-black flex flex-col items-center justify-center font-display rotate-[-12deg] shadow-2xl border-2 border-[#0A0A0A]">
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold">Sealed</span>
                <span className="text-2xl leading-none">★</span>
                <span className="text-[10px] tracking-[0.2em] uppercase font-bold">MMXXVI</span>
              </div>

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
                className="relative bg-[#0F0F0F] border border-[#C9A961]/30 p-6 md:p-8"
              >
                <img
                  src="/assets/images/onboarding_caps.jpeg"
                  alt="Royal corporate gift box with embroidered cap"
                  className="w-full aspect-square object-cover"
                />

                {/* Caption plaque */}
                <div className="mt-6 flex items-end justify-between gap-4 border-t border-[#C9A961]/30 pt-5">
                  <div>
                    <div className="label-eyebrow text-[#C9A961] mb-1">Specimen N° 042</div>
                    <div className="font-display text-xl">The Founder's Edition</div>
                    <div className="text-xs text-white/50 mt-1">Matte-black presentation box · embossed</div>
                  </div>
                  <div className="text-right">
                    <div className="text-xs text-white/40">From</div>
                    <div className="font-display text-2xl text-[#C9A961]">₹88</div>
                    <div className="text-[10px] text-white/40">/ unit</div>
                  </div>
                </div>
              </motion.div>

              {/* Decorative corner brackets */}
              <span className="absolute -top-3 -right-3 w-8 h-8 border-t-2 border-r-2 border-[#C9A961]" />
              <span className="absolute -bottom-3 -left-3 w-8 h-8 border-b-2 border-l-2 border-[#C9A961]" />
            </div>

            {/* Sub-caption */}
            <div className="mt-8 flex items-start gap-3 text-xs text-white/50 max-w-sm">
              <span className="text-[#C9A961] text-base leading-none">§</span>
              <span>
                Each commission is registered, numbered, and accompanied by a hand-signed
                letter of provenance from the atelier.
              </span>
            </div>
          </div>
        </div>

        {/* Bottom regal ticker */}
        <div className="relative z-10 border-t border-[#C9A961]/20 bg-black/40 backdrop-blur overflow-hidden">
          <div className="flex marquee whitespace-nowrap py-3">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="flex shrink-0 gap-10 pr-10 label-eyebrow text-[#C9A961]/70">
                <span>◆ Hand-finished in small batches</span>
                <span>◆ Numbered &amp; sealed</span>
                <span>◆ Mercerised cotton thread</span>
                <span>◆ Brass eyelets</span>
                <span>◆ Letter of provenance</span>
                <span>◆ Custom presentation box</span>
                <span>◆ Hand-finished in small batches</span>
                <span>◆ Numbered &amp; sealed</span>
                <span>◆ Mercerised cotton thread</span>
                <span>◆ Brass eyelets</span>
                <span>◆ Letter of provenance</span>
                <span>◆ Custom presentation box</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
